<script setup lang="ts">
import type { UniqueLabel } from "@lxc/app-device-common";
import { useSerialize } from "@lxc/app-device-common";
import type { Rules } from "async-validator";
import type { Ref } from "vue";
import { computed, ref } from "vue";
import {
  VALIDATION_NUMBER,
  VALIDATION_REGEXP,
} from "~/core/constants/constants";
import LxcError from "~/core/models/LxcError.class";
import { Filters } from "~/core/models/filters";
import {
  NotificationKey,
  showNotificationSuccess,
} from "~/core/utils/notifications";
import uploadUtils from "~/core/utils/upload.utils";
import { useInstantCertificateRenewCsr } from "~/modules/certificate/composables/useInstantCertificateRenewCsr";
import { CACertificateItemType } from "~/modules/certificate/models/CACertificateItemType.enum";
import type { KeystoreCertificateDetail } from "~/modules/certificate/models/KeystoreCertificateDetail.interface";
import type { KeystoreCertificateDetailUpdateForm } from "~/modules/certificate/models/KeystoreCertificateDetailUpdateForm.interface";
import keystoreService from "~/modules/certificate/services/keystore.service";
import { useFetchPkis } from "~/modules/pki/composables/useFetchPkis";
import {
  getUniqueLabelList,
  getValuesFromUniqueLabels,
} from "~/utils/unique-label-tools";
import ILxcLightRotateCw from "~icons/lxc-light/rotate-cw";
import ILxcAlertCircle from "~icons/lxc/alert-circle";
import ILxcInfo from "~icons/lxc/info";

const props = defineProps<{
  alias?: string;
  disabled?: boolean;
  isTagsLoading: boolean;
  tags?: string[] | null;
  tagsError?: LxcError | null;
}>();

const emit = defineEmits(["close", "save"]);

const { t } = useI18n();
const serialize = useSerialize();

const {
  isLoading: isLoadingFetchPki,
  results: resultsPki,
  error: errorFetchPki,
  fetchData: fetchPki,
  setFilter: setPkiFilter,
} = useFetchPkis();

const {
  isLoading: isLoadingInstantCertificateRenewCsr,
  error: errorInstantCertificateRenewCsr,
  result: resultInstantCertificateRenewCsr,
  instantRenewCertificate,
} = useInstantCertificateRenewCsr();

// File must be less than 750MB
const MAX_FILE_SIZE_BYTES_ALLOWED = 750_000_000;

const genericFormRef = ref();
const defaultCertificateForm: KeystoreCertificateDetailUpdateForm = {
  alias: "",
  certificate: "",
  chain: undefined,
  subject: {
    cn: "",
    email: "",
    serialNumber: undefined,
    organizationUnit: undefined,
    organization: undefined,
    locality: undefined,
    region: undefined,
    country: undefined,
  },
  tags: [],
  type: CACertificateItemType.CERTIFICATE,
  subjectAlternativeNames: [],
  extendedKeyUsageIds: [],
  automaticallyRenewed: false,
  associatedPkiConfigurationAlias: undefined,
};

const show = ref(false);

// deep copy of certificate form including the subject to avoid pointer references to the defaultCertificateForm
const certificateForm: Ref<KeystoreCertificateDetailUpdateForm> =
  ref<KeystoreCertificateDetailUpdateForm>({
    ...defaultCertificateForm,
    subject: { ...defaultCertificateForm.subject },
    tags: [...defaultCertificateForm.tags],
    subjectAlternativeNames: [
      ...defaultCertificateForm.subjectAlternativeNames,
    ],
    extendedKeyUsageIds: [...defaultCertificateForm.extendedKeyUsageIds],
  });
const certificateFormRef: Ref = ref();
const files: Ref<FileList | undefined | null> = ref();
const fileProgress: Ref<number | undefined | null> = ref();
const filesUploadError: Ref<Error | undefined | null> = ref();
const isFileError: ComputedRef<boolean> = computed(
  () => filesUploadError.value instanceof Error,
);

function serializeCertificateFormWithoutAssociatedPki(
  keystoreDetailForm: KeystoreCertificateDetailUpdateForm,
) {
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    associatedPkiConfigurationAlias: _associatedPkiConfigurationAlias,
    ...certificateFormWithoutAssociatedPki
  } = keystoreDetailForm;
  return serialize(certificateFormWithoutAssociatedPki);
}

const initCertificateForm: Ref<KeystoreCertificateDetail | undefined | null> =
  ref();
const initCertificateFormStringified: Ref<string> = ref(
  serializeCertificateFormWithoutAssociatedPki(defaultCertificateForm),
);

const isCertificateLoading = ref<boolean>(false);
const isSaving = ref<boolean>(false);
const loadingCertificateError: Ref<LxcError | null | undefined> = ref();
const loadingErrorVisible: ComputedRef<boolean> = computed(() =>
  LxcError.check(loadingCertificateError.value),
);

const isCsr = computed(
  () => certificateForm.value.type === CACertificateItemType.CSR,
);

const errorHeader: ComputedRef<string> = computed(() =>
  isCsr.value
    ? t("certificates.keystore.error.csr.loading")
    : t("certificates.error.certificate.loading"),
);

const edited: ComputedRef<boolean> = computed(() => {
  const stringifiedForm = serializeCertificateFormWithoutAssociatedPki(
    certificateForm.value,
  );
  return (
    stringifiedForm !== initCertificateFormStringified.value ||
    (files.value != null && files.value.length !== 0)
  );
});

const rules: ComputedRef<Rules> = computed(() => {
  const rulesConfig: Rules = {
    alias: [
      {
        required: true,
        message: t("certificates.validation.alias.required"),
        type: "string",
        whitespace: false,
      },
      {
        type: "string",
        message: t("certificates.validation.alias.invalid"),
        pattern: VALIDATION_REGEXP.CERTIFICATE_ALIAS,
      },
    ],
    "subject.cn": [
      {
        required: true,
        message: t("certificates.validation.cn"),
        type: "string",
        whitespace: false,
      },
    ],
    "subject.email": [
      {
        required: true,
        message: t("certificates.validation.email"),
        whitespace: false,
      },
      {
        max: VALIDATION_NUMBER.EMAIL_MAX_LENGTH,
        message: t("input.error.maxLength", {
          maxLength: VALIDATION_NUMBER.EMAIL_MAX_LENGTH,
        }),
      },
      {
        type: "email",
        message: t("input.error.invalidFormat"),
      },
    ],
  };

  if (certificateForm.value.automaticallyRenewed) {
    rulesConfig["associatedPkiConfigurationAlias"] = [
      {
        required: true,
        message: t("certificates.validation.associatedPkiConfiguration"),
        type: "string",
      },
    ];
  }

  return rulesConfig;
});

function cloneCertificateForm(
  targetCertificateForm: Ref<KeystoreCertificateDetailUpdateForm>,
  certificateDetailForm?: KeystoreCertificateDetail | null,
) {
  const certificateTags: UniqueLabel[] | undefined = getUniqueLabelList(
    certificateDetailForm?.tags,
    props.disabled,
  );
  Object.assign(
    targetCertificateForm.value,
    certificateDetailForm ?? defaultCertificateForm,
    {
      subject: {
        ...(certificateDetailForm?.subject ?? defaultCertificateForm.subject),
      },
      tags: [...(certificateTags ?? defaultCertificateForm.tags)],
      subjectAlternativeNames: certificateDetailForm?.subjectAlternativeNames
        ? certificateDetailForm?.subjectAlternativeNames
            ?.slice()
            .map((subjectAlternativeName) => {
              return { ...subjectAlternativeName };
            })
        : [],
      extendedKeyUsageIds: certificateDetailForm?.extendedKeyUsageIds
        ? certificateDetailForm?.extendedKeyUsageIds.slice()
        : [],
    },
  );
}

function setCertificateDetailForm(
  certificateDetailForm?: KeystoreCertificateDetail | null,
) {
  files.value = files.value !== undefined ? null : undefined;
  clearCertificateUploadStatus();
  cloneCertificateForm(certificateForm, certificateDetailForm);
  initCertificateForm.value = certificateDetailForm;
  initCertificateFormStringified.value =
    serializeCertificateFormWithoutAssociatedPki(certificateForm.value);
  // set timeout to be sure that the clearValidate is processed after updating the form inputs
  setTimeout(certificateFormRef.value?.clearValidate, 0);
}

/**
 * Check if the file can be uploaded, show an error if not
 * @param file File to check
 * @return true if the file can be uploaded, false otherwise
 */
function canUploadFile(file?: File): boolean {
  let error: string | undefined;

  if (file === undefined) {
    return false;
  }

  if (!uploadUtils.isCertificateFile(file)) {
    error = "extension";
  } else if (!file.size) {
    error = "fileEmpty";
  } else if (file.size > MAX_FILE_SIZE_BYTES_ALLOWED) {
    error = "fileSize";
  }

  if (error) {
    filesUploadError.value = new Error(
      t(`certificates.validation.certificate.${error}`),
    );
  } else {
    filesUploadError.value =
      filesUploadError.value !== undefined ? null : undefined;
  }

  return !error;
}

function clearCertificateUploadStatus() {
  filesUploadError.value =
    filesUploadError.value !== undefined ? null : undefined;
  fileProgress.value = fileProgress.value !== undefined ? null : undefined;
}

async function getCertificateDetails(alias?: string | null): Promise<void> {
  if (alias) {
    open();

    isCertificateLoading.value = true;
    const response = await keystoreService.getCertificate(alias);

    if (LxcError.check(response)) {
      loadingCertificateError.value = response;
    } else {
      loadingCertificateError.value = null;
      setCertificateDetailForm(response);
    }

    isCertificateLoading.value = false;
  } else {
    close();
  }
}

/**
 * upload the certificate file, parse it in backend and refresh the form with the certificate data
 * the upload progression is voluntary not displayed to the user
 */
async function parseCertificateAndRefreshForm(): Promise<void> {
  const file: File | undefined =
    files.value && files.value.length > 0 ? files.value[0] : undefined;

  if (file && canUploadFile(file)) {
    const response = await keystoreService.parseCertificate(file);

    if (LxcError.check(response)) {
      response.notify(NotificationKey.certificateParsingError);
    } else {
      Object.assign(certificateForm.value, response);
    }
  }
}

async function saveForm(): Promise<void> {
  isSaving.value = true;
  const file: File | undefined = files.value ? files.value[0] : undefined;
  const tags: string[] =
    getValuesFromUniqueLabels(certificateForm.value.tags) ?? [];
  certificateForm.value.alias = certificateForm.value.alias.trim();
  const response = await keystoreService.updateCertificate(
    certificateForm.value.alias,
    fileProgress,
    tags,
    file,
    certificateForm.value.automaticallyRenewed,
  );

  if (LxcError.check(response)) {
    response.notify(NotificationKey.saveError);
  } else {
    showNotificationSuccess(t(NotificationKey.saveSuccess));
    certificateForm.value.type = CACertificateItemType.CERTIFICATE;
    await getCertificateDetails(props.alias);
    emit("save");
  }

  isSaving.value = false;
}

async function validate(): Promise<boolean> {
  let isFormValid: boolean =
    !files?.value?.length || canUploadFile(files?.value[0]);
  const [certificateResult, genericResult] = await Promise.all([
    certificateFormRef.value?.validate().catch(() => false),
    genericFormRef.value?.validate().catch(() => false),
  ]);

  return isFormValid && certificateResult && genericResult;
}

const onSubmit = async (): Promise<void> => {
  const dataValid = await validate();

  if (dataValid) {
    await saveForm();
  }
};

const onInstantRenewalClick = async () => {
  if (props.alias) {
    await instantRenewCertificate(props.alias);

    if (errorInstantCertificateRenewCsr.value) {
      errorInstantCertificateRenewCsr.value.notify(NotificationKey.saveError);
    } else {
      showNotificationSuccess(t(NotificationKey.saveSuccess));
      setCertificateDetailForm(resultInstantCertificateRenewCsr.value);
    }

    emit("save");
  }
};

function open() {
  show.value = true;
}

function close() {
  show.value = false;
  emit("close");
}

const onCancel = () => {
  close();
  setCertificateDetailForm();
};

watch(
  () => files.value,
  () => {
    // case when unselecting the file
    if (!files?.value?.length) {
      // reset the certificate form with the initial certificate data
      Object.assign(certificateForm.value, {
        chain: initCertificateForm?.value?.chain
          ? [...initCertificateForm.value.chain]
          : undefined,
        subject: { ...initCertificateForm?.value?.subject },
        subjectAlternativeNames: initCertificateForm?.value
          ?.subjectAlternativeNames
          ? initCertificateForm?.value?.subjectAlternativeNames
              ?.slice()
              .map((subjectAlternativeName) => {
                return { ...subjectAlternativeName };
              })
          : [],
        extendedKeyUsageIds: initCertificateForm?.value?.extendedKeyUsageIds
          ? initCertificateForm?.value?.extendedKeyUsageIds.slice()
          : [],
      });

      // case when clicking on close error => reset the error
      fileProgress.value = 0;
      filesUploadError.value = null;
    }
  },
);

watch(() => props.alias, getCertificateDetails);

watch(
  () => certificateForm.value.automaticallyRenewed,
  async () => {
    if (props.alias && certificateForm.value.automaticallyRenewed) {
      setPkiFilter(Filters.PKI_TRUST_CHAIN_ALIAS, props.alias);
      await fetchPki();
      certificateForm.value.associatedPkiConfigurationAlias =
        resultsPki.value?.results[0]?.alias;
      await validate();
    }
  },
);
</script>

<template>
  <lxc-side-canvas
    v-model:show="show"
    size="1/3"
    :header="t('certificates.keystore.updateCertificate')"
    :close-tooltip="t('button.close')"
    body-class="!pt-4"
    :confirm-enabled="edited"
    :confirm-title="t('certificates.keystore.updateCertificate')"
    :confirm-message="t('certificates.cancelAction.confirm.message')"
    :confirm-ok-label="t('button.confirm')"
    :confirm-cancel-label="t('button.cancel')"
    @discard="onCancel"
  >
    <div v-if="isCertificateLoading" class="w-full h-5 inset-0 z-10">
      <div class="flex flex-col items-center h-5 justify-center">
        <lxc-loader :size="20" />
      </div>
    </div>
    <lxc-form
      v-else
      ref="certificateFormRef"
      :model="certificateForm"
      :rules="rules"
      @submit.prevent="onSubmit"
    >
      <lxc-alert
        v-if="loadingErrorVisible"
        :icon="ILxcAlertCircle"
        type="error"
      >
        <template #title>
          {{ errorHeader }}
        </template>
        <p>{{ loadingCertificateError?.toError()?.message ?? "" }}</p>
      </lxc-alert>
      <lxc-toggle
        v-model="certificateForm.automaticallyRenewed"
        :label="t('certificates.keystore.automaticRenewal.label')"
        with-border
      />
      <div class="py-4">
        <keystore-csr-file-handler
          v-model:files="files"
          :is-csr="isCsr"
          :alias="alias"
          :is-input-disabled="disabled"
          :upload-error="filesUploadError"
          :upload-progress="fileProgress"
          :is-automatic-certificate-renewal="
            certificateForm.automaticallyRenewed
          "
          @input="parseCertificateAndRefreshForm"
        ></keystore-csr-file-handler>
      </div>

      <container-component
        v-if="certificateForm.automaticallyRenewed"
        :px="0"
        :py="0"
        :loader-size="10"
        :is-loading="isLoadingFetchPki"
        :error="errorFetchPki"
        loader-container-class="!mt-0"
      >
        <lxc-information v-if="isCsr" class="!px-0 mb-4">
          <template #component>
            <i-lxc-info />
          </template>
          <template #body>
            <span class="font-medium">
              {{ t("certificates.keystore.automaticRenewal.csr.info") }}
            </span>
          </template>
        </lxc-information>
        <div class="flex items-center gap-4">
          <lxc-form-item
            prop="associatedPkiConfigurationAlias"
            :label="t('certificates.form.associatedPkiConfiguration')"
            class="flex-1"
          >
            <lxc-input
              v-model="certificateForm.associatedPkiConfigurationAlias"
              type="text"
              disabled
            />
          </lxc-form-item>
          <lxc-button
            v-if="isCsr"
            :title="t('certificates.keystore.automaticRenewal.csr.buttonLabel')"
            :text="t('certificates.keystore.automaticRenewal.csr.buttonLabel')"
            class="self-start mt-7"
            type="secondary"
            :icon="ILxcLightRotateCw"
            :is-loading="isLoadingInstantCertificateRenewCsr"
            :disabled="!certificateForm.associatedPkiConfigurationAlias"
            @click.prevent.stop="onInstantRenewalClick"
          />
        </div>
      </container-component>

      <keystore-generic-generation-form
        v-if="!isCertificateLoading"
        ref="genericFormRef"
        v-model="certificateForm"
        :disabled="disabled"
        :edition="true"
        :is-tags-loading="isTagsLoading"
        :tags="tags"
        :tags-error="tagsError"
      />
    </lxc-form>

    <template #footer>
      <div class="grid grid-cols-[max-content_auto] gap-4">
        <lxc-button
          html-type="button"
          type="secondary"
          :title="t('button.close')"
          @click="onCancel"
        >
          {{ t("button.close") }}
        </lxc-button>
        <lxc-button
          html-type="submit"
          :disabled="
            !edited ||
            isFileError ||
            isSaving ||
            (certificateForm.automaticallyRenewed &&
              !certificateForm.associatedPkiConfigurationAlias)
          "
          :title="t('certificates.keystore.updateCertificate')"
          @click="onSubmit"
        >
          {{ t("certificates.keystore.updateCertificate") }}
        </lxc-button>
      </div>
    </template>
  </lxc-side-canvas>
</template>
